import React, { useEffect } from 'react'
import styled from 'styled-components'
import {
  BackgroundImage,
  Box,
  Card,
  getPaletteColor,
  Flex,
  Hide,
  Text,
  BlockLink
} from 'pcln-design-system'
import {
  useContentful,
  getVariantContent,
  ContentfulRichText,
  IToutType
} from '@pcln/contentful-components'
import gaAnalytics from '@/shared-utils/analytics'
import useSeti from '@/hooks/useSeti'
import CONTENT from './content'

const FlexWithTopBorder = styled(Flex)<{ borderColor: string }>`
  border-top: 6px solid ${props => getPaletteColor(props.borderColor)(props)};
  height: 100%;
`

const BackgroundImageOnRight = styled(BackgroundImage)`
  align-self: flex-start;
  height: 100%;
  object-fit: cover;
  object-position: -40px;
  position: relative;
  border-radius: 0px 12px 16px 0px;
  right: 0;
  background-color: #fff;
`

const BackgroundImageOnTop = styled(BackgroundImage)`
  width: 100%;
  flex-shrink: 0;
  object-fit: cover;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
  background-color: #fff;
`

const BoxLink = styled(BlockLink)`
  height: 100%;
  min-height: 234px;
  &:hover {
    text-decoration: none !important;
    color: inherit;
    overflow: visible;
    box-shadow: rgba(0, 0, 0, 0.08) 0px 0px 6px 0px,
      rgba(0, 0, 0, 0.16) 0px 4px 16px 0px;
    border-width: 1px;
    border-color: rgb(192, 202, 213);
    border-image: initial;
    border-radius: 16px;
  }
`

const PromoBox = ({
  children,
  boxLink,
  ariaLabel,
  googleAnalyticsTrackingOnClick
}: {
  children: React.ReactNode
  boxLink?: string | null
  ariaLabel?: string
  googleAnalyticsTrackingOnClick: Record<string, unknown>
}) => {
  return boxLink ? (
    <BoxLink
      onClick={() => gaAnalytics.fireEvent(googleAnalyticsTrackingOnClick)}
      href={boxLink}
      aria-label={ariaLabel}
    >
      {children}
    </BoxLink>
  ) : (
    <Box minHeight="234px" height="100%">
      {children}
    </Box>
  )
}

function getContentfulPromoContent(promo: IToutType) {
  const { url, ariaLabel, googleAnalyticsTracking } =
    promo.callsToAction?.[0] ?? {}
  const promoImage = promo.image ?? null
  const desktopFocalPoint = promoImage?.desktopFocalPoint ?? 'center'
  const tabletFocalPoint = promoImage?.tabletFocalPoint ?? 'center'
  const promoImageUrl = promoImage?.asset?.file.url
  return {
    headline: promo.headline,
    subheadline: promo.subheadline,
    mobileHeadline: promo.mobileHeadline,
    mobileSubheadline: promo.mobileSubheadline,
    disclaimer: promo.disclaimer,
    desktopImage: promoImageUrl
      ? `${promoImageUrl}?w=330&h=448&f=${desktopFocalPoint}&fit=fill`
      : '',
    tabletImage: promoImageUrl
      ? `${promoImageUrl}?w=660&h=224&f=${tabletFocalPoint}&fit=fill`
      : '',
    boxLink: url,
    ariaLabel,
    googleAnalyticsTrackingOnView: {
      category: promo.googleAnalyticsTracking?.category || 'promotion',
      action: promo.googleAnalyticsTracking?.action,
      label: promo.googleAnalyticsTracking?.label
    },
    googleAnalyticsTrackingOnClick: {
      category: googleAnalyticsTracking?.category,
      action: googleAnalyticsTracking?.action,
      label: googleAnalyticsTracking?.label
    },
    borderTopColor: promo.theme?.borderColor
  }
}

export default function IncredibleTileDesktop() {
  const incredibleTilePromo: any = useContentful('incredibleTile')
  const setiName = useSeti(
    incredibleTilePromo?.experimentName,
    incredibleTilePromo?.experimentName !== undefined
  )
  const promo = incredibleTilePromo?.experimentName
    ? getVariantContent(setiName, incredibleTilePromo)
    : incredibleTilePromo
  const hasContentfulData = promo != null
  const {
    headline,
    subheadline,
    mobileHeadline,
    mobileSubheadline,
    disclaimer,
    desktopImage,
    tabletImage,
    boxLink,
    ariaLabel,
    googleAnalyticsTrackingOnClick,
    googleAnalyticsTrackingOnView,
    borderTopColor
  } = hasContentfulData ? getContentfulPromoContent(promo) : CONTENT

  useEffect(() => {
    if (
      googleAnalyticsTrackingOnView?.action &&
      googleAnalyticsTrackingOnView?.label
    ) {
      gaAnalytics.fireEvent({
        category: googleAnalyticsTrackingOnView.category,
        action: googleAnalyticsTrackingOnView.action,
        label: googleAnalyticsTrackingOnView.label
      })
    }
  }, [googleAnalyticsTrackingOnView])

  return (
    <Card
      boxShadowSize="md"
      mr={[0, null, null]}
      borderRadius="xl"
      borderWidth={0}
      width={[1, null, 1 / 3, null, '410px']}
      color="background.lightest"
    >
      <PromoBox
        boxLink={boxLink}
        googleAnalyticsTrackingOnClick={googleAnalyticsTrackingOnClick}
        ariaLabel={ariaLabel}
      >
        <FlexWithTopBorder
          flexDirection={['row', null, 'column', null, 'row']}
          borderRadius="xl"
          borderColor={borderTopColor || 'primary.base'}
        >
          <Hide xs sm xl xx>
            <BackgroundImageOnTop
              height="115px"
              image={tabletImage}
              borderRadius="lg"
              rounded="top"
            />
          </Hide>
          <Flex
            flexDirection="column"
            height="100%"
            width={1}
            px={3}
            pt={2}
            pb={0}
            justifyContent="space-between"
          >
            <Box>
              <Text
                fontSize={5}
                color="primary"
                fontWeight="bold"
                lineHeight="36px"
              >
                <Hide xs sm data-testid="desktop-incredible-headline">
                  {headline}
                </Hide>
                <Hide md lg xl xxl data-testid="mobile-incredible-headline">
                  {mobileHeadline || headline}
                </Hide>
              </Text>
              <Text fontSize={1} color="text" mt={2} lineHeight="24px">
                <Hide xs sm data-testid="desktop-incredible-subheadline">
                  {subheadline}
                </Hide>
                <Hide md lg xl xxl data-testid="mobile-incredible-subheadline">
                  {mobileSubheadline || subheadline}
                </Hide>
              </Text>
            </Box>
            {hasContentfulData ? (
              <Box mt={[20, null, null, 'auto']}>
                <ContentfulRichText
                  color="darkGray"
                  fontSize={11}
                  body={disclaimer as any}
                />
              </Box>
            ) : (
              <Text color="darkGray" fontSize="11px" mb={3}>
                {disclaimer}
              </Text>
            )}
          </Flex>
          <Hide md lg width={1}>
            <BackgroundImageOnRight height="100%" image={desktopImage} />
          </Hide>
        </FlexWithTopBorder>
      </PromoBox>
    </Card>
  )
}
